import { PanelProps } from '@blueprintjs/core';
import omit from 'lodash/omit';
import { TrackingEventList } from 'portal-sdk';
import * as React from 'react';
import { sendHubspotEvent, usePortalFeatures } from 'vet-bones/utils/portal';

import { projectsActionCreators } from 'src/actions/projects/actionCreators';
import {
  ProjectVirtualGraphToCreate,
  ProjectVirtualGraphsToCreate,
} from 'src/containers/ProjectResourceDialog/ProjectVirtualGraphCreationPanel/types';
import { ProjectResourceDialogPanelProps } from 'src/containers/ProjectResourceDialog/types';
import { useCreateMappings } from 'src/containers/ProjectResourceSuggestionsDialog/hooks/useCreateMappings';
import { useFetchMappingSuggestions } from 'src/containers/ProjectResourceSuggestionsDialog/hooks/useFetchMappingSuggestions';
import {
  ProjectVirtualGraphPanel,
  ProjectVirtualGraphPanelRefAttributes,
} from 'src/containers/ProjectVirtualGraphPanel/ProjectVirtualGraphPanel';
import { sortVirtualGraphs } from 'src/containers/ProjectVirtualGraphPanel/utils/sortVirtualGraphs';
import { connectionSelector } from 'src/selectors/connection';
import { dataSourceNativeQuoteCharacterSelector } from 'src/selectors/dataSourceNativeQuoteCharacter';
import { dataSourceSelector } from 'src/selectors/dataSourceSelector';
import { projectSelector } from 'src/selectors/project';
import { DataSource } from 'src/store/state/dataSources';
import {
  ProjectVirtualGraph,
  ProjectVirtualGraphOptions,
  defaultProjectVirtualGraphOptions,
} from 'src/store/state/projects';
import { uiSlice } from 'src/store/ui/ui.slice';
import { useDidValueChange } from 'src/utils/hooks/useDidValueChange';
import { useIsMounted } from 'src/utils/hooks/useIsMounted';
import { useIsVoiceboxEnabled } from 'src/utils/hooks/useIsVoiceboxEnabled';
import { usePrevious } from 'src/utils/hooks/usePrevious';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';
import { useEvaluateSummary } from 'src/utils/voicebox/hooks/useEvaluateSummary';

export type ProjectVirtualGraphCreationPanelProps =
  ProjectResourceDialogPanelProps & {
    dataSourceId: DataSource['id'];
    initialQueryText?: string;
  };

const getInitialQueryTables = (
  virtualGraphs: ProjectVirtualGraphsToCreate,
  dataSource: DataSource | null
) => {
  const tables = [...(dataSource?.tables || [])];

  Object.values(virtualGraphs).forEach((virtualGraph) => {
    const table = virtualGraph.from;
    if (typeof table === 'object') {
      if (
        !tables.find(
          (tableItem) =>
            tableItem.name === table.name &&
            tableItem.catalog === table.catalog &&
            tableItem.schema === table.schema
        )
      ) {
        tables.push({ ...table });
      }
    }
  });

  return tables;
};

export const ProjectVirtualGraphCreationPanel: React.VFC<
  PanelProps<ProjectVirtualGraphCreationPanelProps>
> = ({ closePanel, dataSourceId, setFooterProps }) => {
  const dispatch = useReduxDispatch();
  const { evaluateSummary } = useEvaluateSummary();
  const isMounted = useIsMounted();
  const { projectId } = useRouterParams();
  const project = useReduxSelector(projectSelector(projectId));

  const connection = useReduxSelector(connectionSelector);
  const dataSource = useReduxSelector(dataSourceSelector(dataSourceId));
  const portalFeatures = usePortalFeatures();
  const useVoiceboxSuggestions = Boolean(
    useIsVoiceboxEnabled() && portalFeatures.voiceboxSuggestions
  );

  // Names stored here
  const [virtualGraphsToCreate, setVirtualGraphsToCreate] =
    React.useState<ProjectVirtualGraphsToCreate>({});

  const tables = getInitialQueryTables(virtualGraphsToCreate, dataSource);
  const isPendingTables = !dataSource?.tables;

  const [isUsingCustomQuery, setIsUsingCustomQuery] =
    React.useState<boolean>(false);
  const [selectedTab, setSelectedTab] = React.useState<string>(
    Object.keys(virtualGraphsToCreate)[0] || ''
  );
  const [errorText, setErrorText] = React.useState('');
  const [virtualGraphOptions, setVirtualGraphOptions] =
    React.useState<ProjectVirtualGraphOptions>({
      ...defaultProjectVirtualGraphOptions,
    });

  const updateVirtualGraphOptions = (
    update: Partial<ProjectVirtualGraphOptions>
  ) => {
    setVirtualGraphOptions((prevState) => ({
      ...prevState,
      ...update,
    }));
  };

  const handleUpdateVirtualGraph = React.useCallback(
    (id, update: Partial<ProjectVirtualGraphToCreate>) => {
      setVirtualGraphsToCreate({
        ...virtualGraphsToCreate,
        [id]: {
          ...virtualGraphsToCreate[id],
          ...update,
        },
      });
    },
    [virtualGraphsToCreate]
  );

  const handleAddVirtualGraph = React.useCallback(
    (virtualGraph: ProjectVirtualGraphToCreate) => {
      setVirtualGraphsToCreate({
        ...virtualGraphsToCreate,
        [virtualGraph.id]: virtualGraph,
      });
    },
    [virtualGraphsToCreate]
  );

  const handleRemoveVirtualGraph = React.useCallback(
    (id) => {
      const newVirtualGraphsToCreate = omit(virtualGraphsToCreate, id);

      // Check if current tab is to be removed
      if (selectedTab && selectedTab === id) {
        if (Object.keys(newVirtualGraphsToCreate).length) {
          setSelectedTab(sortVirtualGraphs(newVirtualGraphsToCreate)[0].id);
        } else {
          setSelectedTab('');
        }
      }
      setVirtualGraphsToCreate(newVirtualGraphsToCreate);
    },
    [selectedTab, virtualGraphsToCreate]
  );

  const nativeQuoteCharacter = useReduxSelector(
    dataSourceNativeQuoteCharacterSelector(dataSource?.type || null, connection)
  );
  const isOpen = useReduxSelector(
    (state) => state.ui.isProjectResourceDialogOpen
  );
  const didIsOpenChange = useDidValueChange(isOpen);

  const abortQueryRef =
    React.useRef<ProjectVirtualGraphPanelRefAttributes>(null);
  React.useEffect(() => {
    if (didIsOpenChange && !isOpen) {
      abortQueryRef.current?.abortQuery();
    }
  }, [didIsOpenChange, isOpen, abortQueryRef]);

  const createVirtualGraphs = React.useCallback(() => {
    if (errorText || !connection?.id || !dataSource) {
      return null;
    }
    const virtualGraphsToBeCreated: Array<
      Omit<ProjectVirtualGraph, 'dateLastUpdated'>
    > = [];

    // Populate the required fields for the vgs to create
    if (isUsingCustomQuery) {
      Object.values(virtualGraphsToCreate).forEach((virtualGraphToCreate) => {
        if (typeof virtualGraphToCreate.from === 'string') {
          const temp = omit(
            virtualGraphToCreate,
            'errors',
            'isSettingsOpenKey'
          );

          virtualGraphsToBeCreated.push({
            ...temp,
            connectionId: connection?.id,
            dataSourceEndpoint: dataSource.options?.['jdbc.url'],
            dataSourceId: dataSource?.id,
            dataSourceType: dataSource?.type,
            options: virtualGraphOptions,
          });
        }
      });
    } else {
      Object.values(virtualGraphsToCreate).forEach((virtualGraphToCreate) => {
        if (typeof virtualGraphToCreate.from === 'object') {
          const temp = omit(
            virtualGraphToCreate,
            'errors',
            'isSettingsOpenKey'
          );

          virtualGraphsToBeCreated.push({
            ...temp,
            connectionId: connection?.id,
            dataSourceEndpoint: dataSource.options?.['jdbc.url'],
            dataSourceId: dataSource?.id,
            dataSourceType: dataSource?.type,
            options: virtualGraphOptions,
          });
        }
      });
    }

    dispatch(
      projectsActionCreators.addedVirtualGraphsToProject(
        projectId,
        ...virtualGraphsToBeCreated.filter(
          (
            virtualGraphToBeCreated
          ): virtualGraphToBeCreated is Omit<
            ProjectVirtualGraph,
            'dateLastUpdated'
          > => virtualGraphToBeCreated !== undefined
        )
      )
    );

    return virtualGraphsToBeCreated;
  }, [
    errorText,
    connection,
    dataSource,
    isUsingCustomQuery,
    dispatch,
    projectId,
    virtualGraphsToCreate,
    virtualGraphOptions,
  ]);

  const handleCreateButtonClick = React.useCallback(() => {
    const virtualGraphs = createVirtualGraphs();
    if (virtualGraphs) {
      sendHubspotEvent(TrackingEventList.DESIGNER_DEFINE_RESOURCE_VIRTUAL);
      dispatch(uiSlice.actions.toggledProjectResourceDialog({ isOpen: false }));
    }
  }, [createVirtualGraphs, dispatch]);

  const { fetchMappingSuggestions, isLoading } = useFetchMappingSuggestions();
  const handleCreateMappings = useCreateMappings();

  const handleCreateAndMapButtonClick = React.useCallback(async () => {
    const virtualGraphs = createVirtualGraphs();
    if (virtualGraphs) {
      const resourceIds = virtualGraphs.map(({ id }) => id);
      sendHubspotEvent(TrackingEventList.DESIGNER_DEFINE_RESOURCE_VIRTUAL);
      const loaded = await fetchMappingSuggestions({
        selectedClasses: [],
        selectedResources: resourceIds,
      });
      if (loaded.mappingSuggestions?.length) {
        handleCreateMappings(loaded.mappingSuggestions);
      }
      if (useVoiceboxSuggestions) {
        evaluateSummary(project);
      }
      dispatch(uiSlice.actions.toggledProjectResourceDialog({ isOpen: false }));
    }
  }, [
    createVirtualGraphs,
    dispatch,
    project,
    fetchMappingSuggestions,
    handleCreateMappings,
    useVoiceboxSuggestions,
  ]);

  React.useEffect(() => {
    if (isMounted.current) {
      setFooterProps({
        createAndMapButtonPending: isLoading,
        createButtonsError: errorText,
        handleBackButtonClick: closePanel,
        handleCreateAndMapButtonClick: !isPendingTables
          ? handleCreateAndMapButtonClick
          : undefined,
        handleCreateButtonClick: !isPendingTables
          ? handleCreateButtonClick
          : undefined,
      });
    }
  }, [
    closePanel,
    isMounted,
    setFooterProps,
    handleCreateButtonClick,
    errorText,
    handleCreateAndMapButtonClick,
    isLoading,
    isPendingTables,
  ]);

  const previousDataSource = usePrevious(dataSource);
  React.useEffect(() => {
    if (!previousDataSource || !dataSource) {
      return;
    }

    if (
      previousDataSource.tables === null &&
      Array.isArray(dataSource.tables) &&
      !dataSource.tables.length
    ) {
      setIsUsingCustomQuery(true);
    }
  }, [dataSource, previousDataSource]);

  return (
    <ProjectVirtualGraphPanel
      dataSource={dataSource}
      handleAddVirtualGraph={handleAddVirtualGraph}
      handleRemoveVirtualGraph={handleRemoveVirtualGraph}
      handleUpdateVirtualGraph={handleUpdateVirtualGraph}
      isPendingTables={isPendingTables}
      isSettingsOpenKey={1}
      isUsingCustomQuery={isUsingCustomQuery}
      nativeQuoteCharacter={nativeQuoteCharacter}
      selectedTab={selectedTab}
      setErrorText={setErrorText}
      setIsUsingCustomQuery={setIsUsingCustomQuery}
      setSelectedTab={setSelectedTab}
      setVirtualGraphOptions={setVirtualGraphOptions}
      tables={tables}
      updateVirtualGraphOptions={updateVirtualGraphOptions}
      virtualGraphOptions={virtualGraphOptions}
      virtualGraphsToCreate={virtualGraphsToCreate}
    />
  );
};
